// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { Button } from '@mui/material';
import {
  createStepsOptions,
  StepsProvider,
  Translation,
  type TriggerProps,
} from '@shared/ui-kit';
import { defineComponent } from '@shared/ui-kit/utils/define-component';
import { AccountWorkflowProvider } from '@shared/advisory/account-workflow/providers';
import { Modal } from '@shared/advisory/account-workflow/layout/modal';
import { AccountUsage } from '@shared/advisory/account-workflow/components/account-opening/account-usage';
import { IdentityVerification } from '@shared/advisory/account-workflow/components/account-opening/identity-verify';
import { LegalDeclarations } from '@shared/advisory/account-workflow/components/account-opening/legal-verify';
import { PersonalData } from '@shared/advisory/account-workflow/components/shared/personal-data';
import { ProfessionalProfile } from '@shared/advisory/account-workflow/components/account-opening/professional-profile';
import { Signature } from '@shared/advisory/account-workflow/components/account-opening/signature';
import { SubmittedRequest } from '@shared/advisory/account-workflow/components/account-opening/submitted-request';
import { WealthSourceFunds } from '@shared/advisory/account-workflow/components/account-opening/wealth-funds';

type AccountOpeningButtonProps = {
  canResumeAccountOpening?: boolean;
};

export const AccountOpeningStepsOptions = createStepsOptions({
  definitions: [
    {
      id: 'personal-data',
      element: <PersonalData />,
      meta: { section: 'personal-data' },
    },
    {
      id: 'professional-profile',
      element: <ProfessionalProfile />,
      meta: { section: 'professional-profile' },
    },
    {
      id: 'wealth-source-funds',
      element: <WealthSourceFunds />,
      meta: { section: 'wealth-source-funds' },
    },
    {
      id: 'account-usage',
      element: <AccountUsage />,
      meta: { section: 'account-usage' },
    },
    {
      id: 'legal-declarations',
      element: <LegalDeclarations />,
      meta: { section: 'legal-declarations' },
    },
    {
      id: 'identity-verification',
      element: <IdentityVerification />,
      meta: { section: 'identity-verification' },
    },
    { id: 'signature', element: <Signature />, meta: { section: 'signature' } },
    {
      id: 'next-steps',
      element: <SubmittedRequest />,
      meta: { section: 'next-steps', hideProgressTracker: true },
    },
  ],
  defaultStep: 'personal-data',
});

export const AccountOpeningButton = defineComponent<
  'AccountOpeningButton',
  AccountOpeningButtonProps
>({
  displayName: 'AccountOpeningButton',
  View: ({ canResumeAccountOpening = false }) => (
    <StepsProvider {...AccountOpeningStepsOptions}>
      <AccountWorkflowProvider workflow="account-opening">
        <Modal
          Trigger={(props: TriggerProps) => (
            <Button
              {...props}
              color="secondary"
              className="!w-full md:!ml-auto md:!w-auto md:!shrink-0"
              data-testid="nuvara-inversis-open-account-button"
            >
              <Translation
                i18nKey={
                  canResumeAccountOpening
                    ? 'aggregator.nuvara-inversis-banner-resume-link'
                    : 'aggregator.nuvara-inversis-banner-link'
                }
                ns="aggregator"
              />
            </Button>
          )}
          modalTestId="account-opening-modal"
        />
      </AccountWorkflowProvider>
    </StepsProvider>
  ),
});
